import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken, getPortalType } from '@/utils/auth';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

/**
 * Quests API service
 * Handles all API calls for the Quests feature with error handling and response formatting
 */

// Guest detection helper
const isGuest = () => {
  if (localStorage.getItem('is_guest_session') === 'true') return true;
  return !getAuthToken(getPortalType(window.location.pathname || ''));
};

// localStorage helpers for guest quests
export const guestQuestHelpers = {
  saveQuest: (questId, questData) => {
    if (!isGuest()) return;
    try {
      const guestQuests = JSON.parse(localStorage.getItem('guest_quests') || '{}');
      guestQuests[questId] = questData;
      localStorage.setItem('guest_quests', JSON.stringify(guestQuests));
    } catch (e) {
      console.error('Error saving guest quest to localStorage:', e);
    }
  },
  getQuest: (questId) => {
    if (!isGuest()) return null;
    try {
      const guestQuests = JSON.parse(localStorage.getItem('guest_quests') || '{}');
      return guestQuests[questId] || null;
    } catch (e) {
      console.error('Error retrieving guest quest from localStorage:', e);
      return null;
    }
  },
  deleteQuest: (questId) => {
    if (!isGuest()) return;
    try {
      const guestQuests = JSON.parse(localStorage.getItem('guest_quests') || '{}');
      delete guestQuests[questId];
      localStorage.setItem('guest_quests', JSON.stringify(guestQuests));
    } catch (e) {
      console.error('Error deleting guest quest from localStorage:', e);
    }
  },
  getAllQuests: () => {
    if (!isGuest()) return {};
    try {
      return JSON.parse(localStorage.getItem('guest_quests') || '{}');
    } catch (e) {
      console.error('Error retrieving all guest quests from localStorage:', e);
      return {};
    }
  },
  saveResults: (questId, resultsData) => {
    // Save results to cache regardless of guest status to support shared links
    try {
      const guestResults = JSON.parse(localStorage.getItem('guest_quest_results') || '{}');
      guestResults[questId] = resultsData;
      localStorage.setItem('guest_quest_results', JSON.stringify(guestResults));
    } catch (e) {
      console.error('Error saving guest results to localStorage:', e);
    }
  },
  getResults: (questId) => {
    // Try to retrieve from cache regardless of guest status
    try {
      const guestResults = JSON.parse(localStorage.getItem('guest_quest_results') || '{}');
      return guestResults[questId] || null;
    } catch (e) {
      console.error('Error retrieving guest results from localStorage:', e);
      return null;
    }
  }
};

// Error handler for API requests
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data, headers } = error.response;

    // Handle rate limiting (429) or payment required (402)
    if (status === 429 || status === 402) {
      // Broadcast an event so other UI (AuthPromptDialog) can open
      try {
        window.dispatchEvent(new CustomEvent('paymentRequired', {
          detail: {
            statusCode: status,
            feature: 'quests',
            message: data?.error || data?.message || (status === 429 ? 'Rate limit exceeded' : 'Payment required')
          }
        }));
      } catch (e) {
        console.error('Error dispatching paymentRequired event:', e);
      }

      const rateLimitError = new Error(status === 429 ? 'Rate limit exceeded' : 'Payment required');
      rateLimitError.status = status;
      rateLimitError.response = error.response;
      rateLimitError.isRateLimit = true;
      throw rateLimitError;
    }

    // Check if this is an offline response from service worker
    if (status === 503 && headers?.['x-service-worker'] === 'offline') {
      return {
        success: false,
        error: 'Offline',
        status: 503,
        offline: true,
        data: data,
        message: data?.message || 'You are currently offline. Please check your internet connection.'
      };
    }

    // Check if response data indicates offline status
    if (data?.offline === true) {
      return {
        success: false,
        error: 'Offline',
        status: status || 503,
        offline: true,
        data: data,
        message: data?.message || 'You are currently offline. Please check your internet connection.'
      };
    }

    const errorMessage = data?.error || data?.message || data?.detail || `Server error (${status})`;

    return {
      success: false,
      error: errorMessage,
      status,
      data: data,
      message: errorMessage
    };
  } else if (error.request) {
    // Request made but no response received (likely offline)
    return {
      success: false,
      error: 'Network error: No response from server',
      offline: true,
      message: 'Unable to connect to server. Please check your internet connection.'
    };
  } else {
    // Error in request setup
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
      message: error.message || 'An unexpected error occurred'
    };
  }
};

// Get authentication headers
const getAuthHeaders = () => {
  const portal = getPortalType(window.location.pathname || '');
  const token = getAuthToken(portal);

  // Return optional headers - guests can make requests without token
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

// Format response data
const formatResponse = (data, message = null) => {
  return {
    success: true,
    data,
    message,
    error: null
  };
};

const buildThrownApiError = (result) => {
  const error = new Error(result?.error || result?.message || 'Request failed');
  if (result?.status) {
    error.status = result.status;
    error.response = {
      status: result.status,
      data: result.data,
    };
  }
  if (result?.data) {
    error.data = result.data;
    error.errorCode = result.data.error_code;
  }
  if (result?.offline) {
    error.offline = true;
  }
  return error;
};

// Retry logic for failed API calls
const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      // Don't retry on 4xx errors (client errors)
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      const waitTime = delay * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, waitTime));

      console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${waitTime}ms`);
    }
  }

  throw lastError;
};

/**
 * Generate a new quest
 * @param {Object} params - Generation parameters
 * @param {number} params.subject - Subject ID
 * @param {number} params.chapter - Chapter ID
 * @param {string} params.mode - 'practice' or 'test'
 * @param {string} params.question_type - Question type
 * @param {string} params.difficulty - Difficulty level
 * @param {string} params.question_source - Question source
 * @param {number} params.num_questions - Number of questions to generate
 * @param {string} params.section_id - Section ID
 * @param {string} params.section_number - Section number
 * @param {string} params.section_name - Section name
 * @param {number} retries - Number of retries (default: 0)
 * @returns {Promise<Object>} Formatted response with quest data
 */
export const generateQuest = async (params, retries = 0) => {
  try {
    // Validate parameters
    const { subject, chapter, mode, question_type, difficulty } = params;
    if (!subject || !chapter || !mode || !question_type || !difficulty) {
      throw new Error('Missing required parameters for quest generation');
    }

    const requestFn = () => axios.post(
      apiUrl('v1/quests/generate/'),
      params,
      {
        headers: getAuthHeaders(),
        timeout: 120000 // Generation can legitimately take longer than regular API calls
      }
    );

    const response = retries > 0
      ? await retryRequest(requestFn, retries)
      : await requestFn();

    // Cache the generated quest for guests
    if (response.data?.id) {
      guestQuestHelpers.saveQuest(response.data.id, response.data);
    }

    return formatResponse(response.data, 'Quest generated successfully');
  } catch (error) {
    console.error('Error generating quest:', error);
    return handleApiError(error);
  }
};

/**
 * Get quest list with filtering and pagination
 * @param {string} status - Filter by status (draft, in_progress, completed)
 * @param {number} page - Page number (default: 1)
 * @param {Object} additionalParams - Additional query parameters (mode, subject, chapter, etc.)
 * @returns {Promise<Object>} Formatted response with quest list
 */
export const getQuestList = async (status = null, page = 1, additionalParams = {}, retries = 2) => {
  try {
    const params = {
      page: page,
      page_size: 20, // Default page size
      ...additionalParams
    };

    // Add status filter if provided
    if (status) {
      params.status = status;
    }

    const response = await retryRequest(
      () => axios.get(
        apiUrl('v1/quests/'),
        {
          params,
          headers: getAuthHeaders()
        }
      ),
      retries
    );

    // Format paginated response
    const data = response.data;
    const formattedData = {
      results: Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []),
      count: data.count || (Array.isArray(data) ? data.length : 0),
      next: data.next || null,
      previous: data.previous || null,
      page: page,
      pageSize: params.page_size
    };

    return formatResponse(formattedData, 'Quests loaded successfully');
  } catch (error) {
    console.error('Error fetching quest list:', error);
    return handleApiError(error);
  }
};

/**
 * Get current question from quest set
 * @param {number|string} questSetId - Quest Set ID
 * @returns {Promise<Object>} Formatted response with current question and progress
 */
export const getCurrentQuestion = async (questSetId) => {
  try {
    if (!questSetId) {
      throw new Error('Quest Set ID is required');
    }

    // First get the quest to find current question number
    const questResponse = await axios.get(
      apiUrl(`v1/quests/${questSetId}/`),
      {
        headers: getAuthHeaders()
      }
    );

    const quest = questResponse.data;
    const progress = quest.progress || {};
    const currentQuestionNumber = progress.current_question_number || 1;

    // Find current question in questions array
    const questions = quest.questions || [];
    const currentQuestion = questions.find(q => q.question_number === currentQuestionNumber);

    if (!currentQuestion) {
      throw new Error('Current question not found');
    }

    // Get answer for current question if exists
    let currentAnswer = null;
    try {
      const answerResponse = await axios.get(
        apiUrl(`v1/quests/${questSetId}/answers/${currentQuestion.id}/`),
        {
          headers: getAuthHeaders()
        }
      );
      currentAnswer = answerResponse.data;
    } catch (answerError) {
      // No answer yet, that's fine
      console.log('No existing answer found for current question');
    }

    const data = {
      question: currentQuestion,
      answer: currentAnswer,
      progress: progress,
      quest: quest
    };

    return formatResponse(data, 'Current question loaded successfully');
  } catch (error) {
    console.error('Error fetching current question:', error);
    return handleApiError(error);
  }
};

/**
 * Submit answer for a question
 * @param {number|string} questSetId - Quest Set ID
 * @param {number|string} questionId - Question ID
 * @param {string} answerText - Answer text (for written/short answer questions)
 * @param {string} selectedOption - Selected option (for MCQ/True-False questions)
 * @returns {Promise<Object>} Formatted response with evaluation results
 */
export const submitAnswer = async (questSetId, questionId, answerText = null, selectedOption = null, retries = 2) => {
  try {
    if (!questSetId || !questionId) {
      throw new Error('Quest Set ID and Question ID are required');
    }

    // Validate that at least one answer is provided
    if (!answerText && !selectedOption) {
      throw new Error('Either answer text or selected option must be provided');
    }

    const answerData = {
      question: questionId,
      answer_text: answerText || '',
      selected_option: selectedOption || null
    };

    const response = await retryRequest(
      () => axios.post(
        apiUrl(`v1/quests/${questSetId}/answers/`),
        answerData,
        {
          headers: getAuthHeaders(),
          timeout: 60000 // 60 seconds timeout for AI evaluation
        }
      ),
      retries
    );

    return formatResponse(response.data, 'Answer submitted and evaluated successfully');
  } catch (error) {
    console.error('Error submitting answer:', error);
    return handleApiError(error);
  }
};

/**
 * Get results for a completed quest
 * @param {number|string} questSetId - Quest Set ID
 * @returns {Promise<Object>} Formatted response with results data
 */
export const getResults = async (questSetId, params = {}) => {
  try {
    if (!questSetId) {
      throw new Error('Quest Set ID is required');
    }

    // Check local cache first (for guest results OR shared results)
    const cachedResults = guestQuestHelpers.getResults(questSetId);
    if (cachedResults && !params.slug && !params.share_slug) { // Only use cache if NOT fetching specific shared instance
      console.log('Quest results retrieved from local cache');
      return formatResponse(cachedResults, 'Results loaded from cache');
    }

    let endpoint = `v1/quests/${questSetId}/results/`;

    // Explicitly append slug to URL to ensure backend receives it
    if (params.slug) {
      endpoint += `?slug=${params.slug}`;
      console.log('Fetching shared results with slug:', params.slug);
    } else if (params.share_slug) {
      endpoint += `?slug=${params.share_slug}`;
      console.log('Fetching shared results with share_slug:', params.share_slug);
    }

    const response = await axios.get(
      apiUrl(endpoint),
      {
        headers: getAuthHeaders(),
        // params: params // Don't pass params if manually appended to avoid duplication or stripping issues
      }
    );

    const data = response.data;

    // Cache results for guests IF it is not a shared link view
    const isSharedLink = params.slug || params.share_slug;
    if (!isSharedLink && (isGuest() || String(questSetId).length > 10)) { 
      guestQuestHelpers.saveResults(questSetId, data);
    }

    // Format results data
    const formattedData = {
      quest: data.quest || null,
      review: data.review || null,
      questions: data.quest?.questions || [],
      answers: data.quest?.student_answers || []
    };

    return formatResponse(formattedData, 'Results loaded successfully');
  } catch (error) {
    console.error('Error fetching results:', error);
    return handleApiError(error);
  }
};

/**
 * Mark quest as revisited with action tracking
 * @param {number|string} questSetId - Quest Set ID
 * @param {string} action - Action type ('resumed', 'reviewed', 'continued', 'restarted')
 * @returns {Promise<Object>} Formatted response
 */
export const markQuestAsRevisited = async (questSetId, action) => {
  try {
    if (!questSetId || !action) {
      throw new Error('Quest Set ID and action are required');
    }

    // Validate action
    const validActions = ['resumed', 'reviewed', 'continued', 'restarted'];
    if (!validActions.includes(action)) {
      throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
    }

    const response = await axios.post(
      apiUrl(`v1/quests/${questSetId}/revisit/`),
      { action },
      {
        headers: getAuthHeaders()
      }
    );

    return formatResponse(response.data, `Quest marked as ${action} successfully`);
  } catch (error) {
    console.error('Error marking quest as revisited:', error);
    return handleApiError(error);
  }
};

/**
 * Export questsAPI object for backward compatibility
 * This maintains the existing API structure while using new functions internally
 */
/**
 * Check if questions exist in question bank for a chapter
 * @param {number} chapterId - Chapter ID
 * @param {Object|null} section - Optional section metadata
 * @returns {Promise<Object>} Response with has_questions and count
 */
export const checkQuestionBankAvailability = async (chapterId, section = null) => {
  try {
    if (!chapterId) {
      throw new Error('Chapter ID is required');
    }

    const normalizedSectionId = section?.section_id || section?.id;
    const response = await axios.get(
      apiUrl('v1/quests/question-bank/check/'),
      {
        params: {
          chapter_id: chapterId,
          ...(normalizedSectionId && normalizedSectionId !== 'undefined' && normalizedSectionId !== 'null'
            ? { section_id: normalizedSectionId }
            : {}),
          ...(section?.section_number ? { section_number: section.section_number } : {}),
          ...(section?.section_name ? { section_name: section.section_name } : {}),
        },
        headers: getAuthHeaders()
      }
    );

    return formatResponse(response.data, 'Question bank availability checked');
  } catch (error) {
    console.error('Error checking question bank availability:', error);
    return handleApiError(error);
  }
};

export const questsAPI = {
  /**
   * Generate a new quest set (wrapper for generateQuest)
   */
  generate: async (questData) => {
    // Pass everything in questData to generateQuest (params, retries)
    const result = await generateQuest(questData, 0);
    if (result.success) {
      return result.data;
    }
    throw buildThrownApiError(result);
  },

  /**
   * List all quests with optional filtering (wrapper for getQuestList)
   */
  list: async (params = {}) => {
    const { status, page, ...additionalParams } = params;
    const result = await getQuestList(status, page || 1, additionalParams);
    if (result.success) {
      // Return the full paginated payload so callers can access pagination metadata
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Get a single quest by ID
   */
  get: async (questId) => {
    try {
      // Try to get from localStorage first for guests (only if not UUID, or maybe we should always check server for UUID?)
      // We should check server first for UUIDs to get latest status/results especially after completion
      if (isGuest()) {
        const isUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(questId);
        if (!isUuid) {
          const cachedQuest = guestQuestHelpers.getQuest(questId);
          if (cachedQuest) {
            console.log('Quest retrieved from localStorage cache');
            return cachedQuest;
          }
        }
      }

      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/`),
        {
          headers: getAuthHeaders()
        }
      );

      // Update cache for guests with latest from server
      if (response.data?.id) {
        guestQuestHelpers.saveQuest(response.data.id, response.data);
      }

      return response.data;
    } catch (error) {
      if (error.response?.status === 404 && isGuest()) {
        // For guests, check localStorage one more time
        const cachedQuest = guestQuestHelpers.getQuest(questId);
        if (cachedQuest) {
          console.warn('Quest not found on server, returning from localStorage cache');
          return cachedQuest;
        }
        console.warn('Quest not found - likely cache expired. Returning null.');
        try {
          trackEvent('guest_session_expired', attachUserProps({
            page: 'quests',
            quest_id: questId,
            source: 'quest_retrieve',
          }));
        } catch {}
        return null;
      }
      console.error('Error fetching quest:', error);
      const errorResult = handleApiError(error);
      const wrappedError = new Error(errorResult.error || errorResult.message);
      wrappedError.status = errorResult.status || error.response?.status || null;
      wrappedError.data = errorResult.data || error.response?.data || null;
      wrappedError.response = error.response || null;
      wrappedError.offline = Boolean(errorResult.offline);
      throw wrappedError;
    }
  },

  /**
   * Update a quest
  /**
   * Update a quest
   */
  update: async (questId, updates) => {
    try {
      // Handle guest updates - try backend first if it's a UUID (server-side guest session)
      if (isGuest()) {
        const isUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(questId);

        if (isUuid) {
          try {
            // Try backend update for guest UUID quests (needed for result calculation)
            const response = await axios.patch(
              apiUrl(`v1/quests/${questId}/`),
              updates,
              { headers: getAuthHeaders() } // Will remove token if guest
            );
            // Update local storage with backend response
            guestQuestHelpers.saveQuest(questId, response.data);
            // Also save results if included
            if (response.data.review) {
              guestQuestHelpers.saveResults(questId, response.data);
            }
            return response.data;
          } catch (backendError) {
            console.warn('Backend update failed for guest quest, falling back to local update:', backendError);
            // Fallback to local update below
          }
        }

        // Local-only update fallback (or for non-UUID guest quests)
        const quest = guestQuestHelpers.getQuest(questId);
        if (quest) {
          const updatedQuest = { ...quest, ...updates, updated_at: new Date().toISOString() };
          guestQuestHelpers.saveQuest(questId, updatedQuest);
          return updatedQuest;
        }
      }

      const response = await axios.patch(
        apiUrl(`v1/quests/${questId}/`),
        updates,
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating quest:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Delete a quest
   */
  delete: async (questId) => {
    try {
      const response = await axios.delete(
        apiUrl(`v1/quests/${questId}/`),
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting quest:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Record revisit action (wrapper for markQuestAsRevisited)
   */
  revisit: async (questId, data) => {
    const result = await markQuestAsRevisited(questId, data.action);
    if (result.success) {
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Get quest results (wrapper for getResults)
   */
  getResults: async (questId, params = {}) => {
    const result = await getResults(questId, params);
    if (result.success) {
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Navigate to next/previous question
   */
  navigate: async (questId, data) => {
    try {
      const response = await axios.post(
        apiUrl(`v1/quests/${questId}/navigation/`),
        data,
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error navigating:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Submit answer for evaluation (wrapper for submitAnswer)
   */
  submitAnswer: async (questId, answerData) => {
    const result = await submitAnswer(
      questId,
      answerData.question,
      answerData.answer_text,
      answerData.selected_option
    );
    if (result.success) {
      return result.data;
    }
    throw new Error(result.error || result.message);
  },

  /**
   * Update/retry answer
   * Uses PATCH instead of PUT for better compatibility with Django REST Framework custom actions
   */
  updateAnswer: async (questId, answerId, updates) => {
    try {
      // Try PATCH first (preferred for partial updates)
      try {
        const response = await axios.patch(
          apiUrl(`v1/quests/${questId}/answers/${answerId}/`),
          updates,
          {
            headers: getAuthHeaders(),
            timeout: 60000 // 60 seconds timeout for AI evaluation
          }
        );
        return response.data;
      } catch (patchError) {
        // Fallback to PUT if PATCH fails
        if (patchError.response?.status === 405) {
          const response = await axios.put(
            apiUrl(`v1/quests/${questId}/answers/${answerId}/`),
            updates,
            {
              headers: getAuthHeaders(),
              timeout: 60000 // 60 seconds timeout for AI evaluation
            }
          );
          return response.data;
        }
        throw patchError;
      }
    } catch (error) {
      console.error('Error updating answer:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Get correct answer for a question
   */
  getCorrectAnswer: async (questId, questionId) => {
    try {
      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/answers/${questionId}/`),
        {
          headers: getAuthHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching correct answer:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  },

  /**
   * Get answer hint for a question
   */
  getAnswerHint: async (questId, questionId) => {
    try {
      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/questions/${questionId}/answer-hint/`),
        {
          headers: getAuthHeaders()
        }
      );
      return formatResponse(response.data, 'Answer hint retrieved successfully');
    } catch (error) {
      console.error('Error fetching answer hint:', error);
      return handleApiError(error);
    }
  },

  /**
   * Get question hint for a question
   */
  getQuestionHint: async (questId, questionId) => {
    try {
      const response = await axios.get(
        apiUrl(`v1/quests/${questId}/questions/${questionId}/question-hint/`),
        {
          headers: getAuthHeaders()
        }
      );
      return formatResponse(response.data, 'Question hint retrieved successfully');
    } catch (error) {
      console.error('Error fetching question hint:', error);
      return handleApiError(error);
    }
  },

  /**
   * Save answer as draft (auto-save without evaluation)
   */
  saveDraftAnswer: async (questId, answerData) => {
    try {
      const response = await axios.patch(
        apiUrl(`v1/quests/${questId}/answers/draft/`),
        {
          question: answerData.question,
          answer_text: answerData.answer_text || '',
          selected_option: answerData.selected_option || null
        },
        {
          headers: getAuthHeaders(),
          timeout: 10000 // 10 seconds timeout for draft save
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving draft answer:', error);
      // Don't throw error for draft saves - fail silently
      return null;
    }
  },

  /**
   * Save progress in bulk (all answers + elapsed time)
   */
  /**
   * Save progress in bulk (all answers + elapsed time)
   */
  saveProgress: async (questId, progressData) => {
    try {
      // Handle guest progress save locally
      if (isGuest() || String(questId).startsWith('guest-')) {
        const quest = guestQuestHelpers.getQuest(questId);
        if (quest) {
          // Update quest with progress data
          // progressData.answers is array of {question, answer_text, selected_option}
          // We need to merge this into quest.student_answers
          let currentAnswers = quest.student_answers || [];

          if (progressData.answers && Array.isArray(progressData.answers)) {
            progressData.answers.forEach(newAns => {
              // Remove old answer for same question
              currentAnswers = currentAnswers.filter(a => {
                const qId = typeof a.question === 'object' ? a.question.id : a.question;
                return String(qId) !== String(newAns.question);
              });
              // Add new answer
              currentAnswers.push({
                question: newAns.question,
                answer_text: newAns.answer_text,
                selected_option: newAns.selected_option,
                attempt_number: 1
              });
            });
          }

          const progress = quest.progress || {};
          const updatedProgress = {
            ...progress,
            current_question_number: progressData.current_question_number || progress.current_question_number || 1,
            elapsed_time_seconds: progressData.elapsed_time_seconds || progress.elapsed_time_seconds || 0,
            last_accessed_at: new Date().toISOString()
          };

          const updatedQuest = {
            ...quest,
            student_answers: currentAnswers,
            answered_count: currentAnswers.length,
            progress: updatedProgress,
            updated_at: new Date().toISOString()
          };

          guestQuestHelpers.saveQuest(questId, updatedQuest);

          return {
            success: true,
            progress: updatedProgress,
            answers: currentAnswers
          };
        }
      }

      const response = await axios.post(
        apiUrl(`v1/quests/${questId}/save-progress/`),
        {
          answers: progressData.answers || [],
          elapsed_time_seconds: progressData.elapsed_time_seconds,
          current_question_number: progressData.current_question_number
        },
        {
          headers: getAuthHeaders(),
          timeout: 30000 // 30 seconds timeout for bulk save
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving progress:', error);
      const errorResult = handleApiError(error);
      throw new Error(errorResult.error || errorResult.message);
    }
  }
};

// Export new functions for direct use
export default {
  generateQuest,
  getQuestList,
  getCurrentQuestion,
  submitAnswer,
  getResults,
  markQuestAsRevisited,
  questsAPI // For backward compatibility
};
